import type { Location } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';
import {
  FHIR_LOCATION_ID_SYSTEM,
  FHIR_TIMEZONE_EXTENSION,
} from '@eluve/fhir-utils';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { Field } from '../../components/Field';
import type { FhirView } from '../../core/view-contract';
import { conceptText } from '../accessors';
import { formatFhirDateTime } from '../observation/format';

/**
 * The exported Location component. Location hierarchies render as trees of
 * rows (the app owns the tree layout via partOf grouping), so the row is the
 * primary variant.
 */
export const LocationView: FhirView<Location> = (props) => {
  if (props.ctx.inList) return <LocationRowView {...props} />;
  return <LocationReadView {...props} />;
};

function locationName(data: Location): string {
  return data.name ?? 'Location';
}

/** The eluve layer name rides on physicalType (text wins over coding display). */
function layerText(data: Location): string | undefined {
  return conceptText(data.physicalType);
}

/** Tree/list row: name, layer, status when not active. */
const LocationRowView: FhirView<Location> = ({ data }) => {
  const layer = layerText(data);
  return (
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      data-view="location-row"
      className="min-w-0 py-2"
    >
      <Text inline size="sm" weight="regular" className="truncate">
        {locationName(data)}
      </Text>
      {layer && (
        <Text inline size="xs" color="tertiary">
          {layer}
        </Text>
      )}
      {data.status !== 'active' && <FhirStatusTag status={data.status} />}
    </Stack>
  );
};

/**
 * Standalone card: identity header plus the fact grid the eluve mirror
 * writes — its durable identifier, contact details, the scheduling timezone,
 * and the parent it hangs off in the location tree.
 */
const LocationReadView: FhirView<Location> = ({ data, ctx }) => {
  const layer = layerText(data);
  return (
    <div
      data-view="location-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text inline size="sm" weight="semibold" className="truncate">
            {locationName(data)}
          </Text>
          {layer && (
            <Text inline size="xs" color="tertiary">
              {layer}
            </Text>
          )}
        </Stack>
        <FhirStatusTag status={data.status} />
      </Stack>
      {/* Field: typed path drives extraction + policy identity + DAR + display —
          an absent value renders its honest state, never a bare em-dash. */}
      <Grid columns={1} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Address" data={data} path={['address', 'text']} />
        <Field
          label="Phone"
          data={data}
          path={['telecom', { system: 'phone' }, 'value']}
        />
        <Field
          label="Timezone"
          data={data}
          path={['extension', { url: FHIR_TIMEZONE_EXTENSION }, 'valueCode']}
        />
        {/* Name first (the display snapshot the mirror writes), reference
            underneath — the snapshot can go stale, the reference cannot. */}
        <Field
          label="Part of"
          data={data}
          path={['partOf', 'reference']}
          render={(value) =>
            data.partOf?.display ? (
              <Stack gap={0} align="start">
                <Text inline size="sm">
                  {data.partOf.display}
                </Text>
                <Text inline size="xs" color="tertiary" className="font-mono">
                  {value}
                </Text>
              </Stack>
            ) : (
              value
            )
          }
        />
        <Field
          label="Eluve location ID"
          data={data}
          path={['identifier', { system: FHIR_LOCATION_ID_SYSTEM }, 'value']}
        />
      </Grid>
      {/* Not a Field: `meta` is resource plumbing, absent from the rendering
          manifest, and never subject to field-level withholding. */}
      {data.meta?.lastUpdated && (
        <Text size="xs" color="tertiary" className="mt-3">
          Last synced {formatFhirDateTime(data.meta.lastUpdated)}
        </Text>
      )}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
